import { Button } from "@frontend/components/ui/button"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@frontend/components/ui/card"
import { DataTable } from "@frontend/components/ui/data-table"
import { type ApiKeyItem, useApiKeys, useRevokeApiKey } from "@frontend/pages/settings/hooks/use-api-keys"
import type { ColumnDef } from "@tanstack/react-table"
import { Key, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { CreateApiKeyDialogContainer } from "./create-api-key-dialog"

export interface ApiKeyListViewProps {
  isLoading: boolean
  keys: ApiKeyItem[]
  confirmRevoke: string | null
  isRevoking: boolean
  onOpenCreate: () => void
  onStartRevoke: (keyId: string) => void
  onConfirmRevoke: (keyId: string) => void
  onCancelRevoke: () => void
}

export function ApiKeyListView({
  isLoading,
  keys,
  confirmRevoke,
  isRevoking,
  onOpenCreate,
  onStartRevoke,
  onConfirmRevoke,
  onCancelRevoke
}: ApiKeyListViewProps) {
  const columns: ColumnDef<ApiKeyItem>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>
    },
    {
      id: "key",
      accessorFn: (row) => `${row.prefix}${row.last_four}`,
      header: "Key",
      cell: ({ row }) => (
        <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
          {row.original.prefix}...{row.original.last_four}
        </code>
      )
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-muted-foreground">{new Date(row.original.created_at).toLocaleDateString()}</span>
      )
    },
    {
      accessorKey: "expires_at",
      header: "Expires",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.expires_at ? new Date(row.original.expires_at).toLocaleDateString() : "Never"}
        </span>
      )
    },
    {
      id: "actions",
      accessorFn: (row) => row.id,
      header: "",
      cell: ({ row }) => (
        <div className="w-full flex justify-end">
          {confirmRevoke === row.original.id ? (
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onConfirmRevoke(row.original.id)}
                disabled={isRevoking}
              >
                {isRevoking ? "Revoking..." : "Confirm"}
              </Button>
              <Button variant="ghost" size="sm" onClick={onCancelRevoke} disabled={isRevoking}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onStartRevoke(row.original.id)}
              title="Revoke API key"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      )
    }
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Keys</CardTitle>
        <CardDescription>
          Manage API keys for SDK and programmatic access. Keys inherit your permissions.
        </CardDescription>
        <CardAction>
          <Button size="sm" onClick={onOpenCreate}>
            <Plus className="h-4 w-4" />
            Create API Key
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm py-4 text-center">Loading keys...</p>
        ) : keys.length === 0 ? (
          <div className="text-center py-8 space-y-2">
            <Key className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground text-sm">No API keys. Create one for SDK access.</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={keys}
            emptyMessage="No API keys. Create one for SDK access."
            showPagination={false}
          />
        )}
      </CardContent>
    </Card>
  )
}

export function ApiKeyListContainer() {
  const { data, isLoading } = useApiKeys()
  const revokeKey = useRevokeApiKey()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  const keys = data?.data ?? []

  const handleRevoke = (keyId: string) => {
    revokeKey.mutate(keyId, {
      onSuccess: () => {
        setConfirmRevoke(null)
      }
    })
  }

  return (
    <>
      <ApiKeyListView
        isLoading={isLoading}
        keys={keys}
        confirmRevoke={confirmRevoke}
        isRevoking={revokeKey.isPending}
        onOpenCreate={() => setCreateDialogOpen(true)}
        onStartRevoke={setConfirmRevoke}
        onConfirmRevoke={handleRevoke}
        onCancelRevoke={() => setConfirmRevoke(null)}
      />

      <CreateApiKeyDialogContainer open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
    </>
  )
}

export const ApiKeyList = ApiKeyListContainer
